import { BlockStack, Box, Tag, Text, TextContainer } from '@shopify/polaris'

import { BulkActionSubscriptionUpdateMethod } from '../common/bulk-actions'
import { pluralize, ShortDateTime, sortAnchors } from '../common/helpers'
import { finishingBehaviorLabels, nextActionOptions } from '../common/subscription-models'
import { DetailsItem } from '../subscription-page/DetailsItem'
import { anchorLabel } from '../subscription-page/ScheduleCard'
import {
  AddProductPayload,
  ApplyDiscountCodePayload,
  EditSchedulePayload,
  isEditScheduleFinishingEnabled,
  isEditScheduleFrequencyEnabled,
  isEditScheduleMinMaxEnabled,
  RemoveProductPayload,
  SwapProductPayload,
  UpdateNextActionPayload,
  UpdateShippingMethodPayload,
} from './common'

type SubscriptionsLabelPosition = 'prefix' | 'postfix'

const SubscriptionsLabel = ({
  total,
  position,
}: {
  total: number
  position?: SubscriptionsLabelPosition
}) => (
  <>
    {position === 'postfix' && ' '}
    <strong>{total}</strong> selected {pluralize(total, 'subscription')}
    {position === 'prefix' && ' '}
  </>
)

const SimpleActionSummary = ({
  subLabelPos,
  total,
  children,
}: {
  subLabelPos: SubscriptionsLabelPosition
  total: number
  children: any
}) => (
  <>
    {subLabelPos === 'prefix' && <SubscriptionsLabel total={total} position="prefix" />}
    {children}
    {subLabelPos === 'postfix' && <SubscriptionsLabel total={total} position="postfix" />}.
  </>
)

const UpdateNextActionSummary = ({
  total,
  payload,
}: {
  total: number
  payload: UpdateNextActionPayload
}) => {
  const action = (
    nextActionOptions.find(
      (opt: any) => opt.value === (payload as UpdateNextActionPayload).action
    ) as any
  ).label.toLowerCase()

  return (
    <SimpleActionSummary subLabelPos="prefix" total={total}>
      will be scheduled to <b>{action}</b> on{' '}
      <b>
        <ShortDateTime date={payload.date} />
      </b>
    </SimpleActionSummary>
  )
}

const UpdateShippingMethodSummary = ({
  total,
  payload,
}: {
  total: number
  payload: UpdateShippingMethodPayload
}) => (
  <SimpleActionSummary subLabelPos="postfix" total={total}>
    The{' '}
    {payload.title && (
      <>
        shipping method <b>title</b> will be changed to <b>{payload.title}</b>
      </>
    )}
    {payload.title && payload.price && ' and the '}
    {payload.price && (
      <>
        shipping <b>price</b> will be changed to <b>{payload.price}</b> (in the current subscription
        currency)
      </>
    )}{' '}
    in
  </SimpleActionSummary>
)

const EditScheduleSummary = ({
  total,
  payload,
}: {
  total: number
  payload: EditSchedulePayload
}) => {
  const frequencyEnabled = isEditScheduleFrequencyEnabled(payload)
  const minMaxEnabled = isEditScheduleMinMaxEnabled(payload)
  const finishingEnabled = isEditScheduleFinishingEnabled(payload)

  const anchors = sortAnchors(payload.anchors ?? [])
  const intervalCount = payload.deliveryIntervalCount ?? 1
  const interval = payload.interval

  const deliveryFrequencyLabel =
    anchors.length > 1 && intervalCount === 1
      ? 'On anchors'
      : `${intervalCount} ${pluralize(intervalCount, (interval ?? '').toLowerCase())}`

  const prePaidDeliveries = payload.prePaidDeliveries ?? 1

  return (
    <BlockStack gap="200">
      <Text as="p">
        <SimpleActionSummary subLabelPos="prefix" total={total}>
          will have their schedule updated
        </SimpleActionSummary>
      </Text>
      <Box maxWidth="300px" background="bg-fill-secondary" borderRadius="200" padding="200">
        <BlockStack gap="100">
          {frequencyEnabled && (
            <>
              <DetailsItem label="Delivery frequency">{deliveryFrequencyLabel}</DetailsItem>
              {anchors.length > 0 && (
                <DetailsItem label="Delivery anchors">
                  <BlockStack gap="100">
                    {anchors.map((anchor, i) => (
                      <Tag key={i}>{anchorLabel(anchor)}</Tag>
                    ))}
                  </BlockStack>
                </DetailsItem>
              )}
              <DetailsItem label="Bill every">
                {prePaidDeliveries} {pluralize(prePaidDeliveries, 'delivery', 'deliveries')}
              </DetailsItem>
            </>
          )}
          {minMaxEnabled && (
            <>
              <DetailsItem label="Min payments">{payload.minCycles || 'not set'}</DetailsItem>
              <DetailsItem label="Max payments">{payload.maxCycles || 'not set'}</DetailsItem>
            </>
          )}
          {finishingEnabled && payload.finishingBehavior && (
            <DetailsItem label="Finishing behavior">
              {finishingBehaviorLabels[payload.finishingBehavior]}
            </DetailsItem>
          )}
        </BlockStack>
      </Box>
    </BlockStack>
  )
}

const BulkActionSummary = ({
  method,
  total,
  payload,
}: {
  method: BulkActionSubscriptionUpdateMethod
  total: number
  payload: any
}) => {
  switch (method) {
    case 'activate':
      return (
        <SimpleActionSummary subLabelPos="prefix" total={total}>
          will be <b>activated</b>
        </SimpleActionSummary>
      )
    case 'pause':
      return (
        <SimpleActionSummary subLabelPos="prefix" total={total}>
          will be <b>paused</b>
        </SimpleActionSummary>
      )
    case 'resume':
      return (
        <SimpleActionSummary subLabelPos="prefix" total={total}>
          will be <b>resumed</b>
        </SimpleActionSummary>
      )
    case 'cancel':
      return (
        <SimpleActionSummary subLabelPos="prefix" total={total}>
          will be <b>cancelled</b>
        </SimpleActionSummary>
      )
    case 'attempt-billing':
      return (
        <SimpleActionSummary subLabelPos="prefix" total={total}>
          will <b>charge</b> for the next order <b>immediately</b>
        </SimpleActionSummary>
      )
    case 'skip-order':
      return (
        <SimpleActionSummary subLabelPos="prefix" total={total}>
          will <b>skip</b> the next order
        </SimpleActionSummary>
      )
    case 'update-next-action':
      return <UpdateNextActionSummary total={total} payload={payload} />

    case 'add-product':
      return (
        <SimpleActionSummary subLabelPos="postfix" total={total}>
          <b>{(payload as AddProductPayload).variantTitle}</b> will be <b>added</b> to
        </SimpleActionSummary>
      )
    case 'swap-product':
      return (
        <SimpleActionSummary subLabelPos="postfix" total={total}>
          All line items with <b>{(payload as SwapProductPayload).sourceVariantTitle}</b> will be{' '}
          <b>swapped</b> to <b>{(payload as SwapProductPayload).variantTitle}</b> in
        </SimpleActionSummary>
      )
    case 'remove-product':
      return (
        <SimpleActionSummary subLabelPos="postfix" total={total}>
          All line items with <b>{(payload as RemoveProductPayload).variantTitle}</b> will be{' '}
          <b>removed</b> from
        </SimpleActionSummary>
      )
    case 'update-shipping-method':
      return <UpdateShippingMethodSummary total={total} payload={payload} />

    case 'edit-schedule':
      return <EditScheduleSummary total={total} payload={payload} />

    case 'apply-discount-code':
      return (
        <SimpleActionSummary subLabelPos="postfix" total={total}>
          The <b>{(payload as ApplyDiscountCodePayload).code}</b> discount code will be{' '}
          <b>applied</b> to
        </SimpleActionSummary>
      )
    case 'remove-discount':
      return (
        <SimpleActionSummary subLabelPos="postfix" total={total}>
          The active discount will be <b>removed</b> from
        </SimpleActionSummary>
      )
    default:
      return null
  }
}

export const SummarySection = ({
  method,
  total,
  payload,
}: {
  method: BulkActionSubscriptionUpdateMethod
  total: number
  payload: any
}) => (
  <TextContainer>
    {method === 'edit-schedule' ? (
      <BulkActionSummary method={method} total={total} payload={payload} />
    ) : (
      <p>
        <BulkActionSummary method={method} total={total} payload={payload} />
      </p>
    )}
    <p>
      <Text as="span" tone="subdued">
        Note: this bulk action will only be attempted on subscriptions which are eligible to it.
      </Text>
    </p>
  </TextContainer>
)
